import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import Chart from "react-apexcharts";
import Loader from "../../Loader";

const GET_ANALYTICS_DAU = gql`
  query getAnalyticsDAU($startDate: String!, $endDate: String!) {
    getAnalyticsDAU(startDate: $startDate, endDate: $endDate) {
      status
      message
      data {
        date
        uniqueUsers
      }
    }
  }
`;

const GET_ANALYTICS_FUNNEL = gql`
  query getAnalyticsFunnel(
    $eventNames: [String!]!
    $startDate: String!
    $endDate: String!
  ) {
    getAnalyticsFunnel(
      eventNames: $eventNames
      startDate: $startDate
      endDate: $endDate
    ) {
      status
      message
      data {
        eventName
        uniqueUsers
      }
    }
  }
`;

const GET_ANALYTICS_PLATFORM_BREAKDOWN = gql`
  query getAnalyticsPlatformBreakdown($startDate: String!, $endDate: String!) {
    getAnalyticsPlatformBreakdown(startDate: $startDate, endDate: $endDate) {
      status
      message
      data {
        platform
        uniqueUsers
      }
    }
  }
`;

const GET_ANALYTICS_EVENT_BREAKDOWN = gql`
  query getAnalyticsEventBreakdown(
    $eventName: String!
    $groupByProperty: String!
    $startDate: String!
    $endDate: String!
    $limit: Int
  ) {
    getAnalyticsEventBreakdown(
      eventName: $eventName
      groupByProperty: $groupByProperty
      startDate: $startDate
      endDate: $endDate
      limit: $limit
    ) {
      status
      message
      data {
        groupValue
        count
      }
    }
  }
`;

const GET_ANALYTICS_EVENT_USERS = gql`
  query getAnalyticsEventUsers(
    $eventName: String!
    $groupByProperty: String!
    $groupValue: String!
    $startDate: String!
    $endDate: String!
    $limit: Int
  ) {
    getAnalyticsEventUsers(
      eventName: $eventName
      groupByProperty: $groupByProperty
      groupValue: $groupValue
      startDate: $startDate
      endDate: $endDate
      limit: $limit
    ) {
      status
      message
      data {
        userId
        fullName
        contactNumber
        propertiesJson
        lastEventAt
        contacted
        notes
        contactedAt
        existingBookingNote
      }
    }
  }
`;

// Reused as-is from the drop-off explorer's mutation — the contacted flag is
// global per-user, not scoped to a from/to pair, so tagging along with
// fromEvent/toEvent here just for context is fine.
const SET_ANALYTICS_DROPOFF_CONTACT = gql`
  mutation setAnalyticsDropoffContact(
    $userId: String!
    $fromEvent: String!
    $toEvent: String!
    $contacted: Boolean!
    $notes: String
  ) {
    setAnalyticsDropoffContact(
      userId: $userId
      fromEvent: $fromEvent
      toEvent: $toEvent
      contacted: $contacted
      notes: $notes
    ) {
      status
      message
    }
  }
`;

// Booking journey in the order users actually move through it. Exported so
// DropoffExplorerWidget can offer the same steps as from/to picks.
// Note: checkout.started fires on CartSummaryPage's initState (page load),
// and slots.requested fires later once the user picks a date/time on that
// same page — so checkout.started chronologically comes first.
export const FUNNEL_EVENT_NAMES = [
  "cart.viewed",
  "checkout.started",
  "slots.requested",
  "booking.completed",
];

export const FUNNEL_LABELS = {
  "cart.viewed": "Viewed Cart",
  "checkout.started": "Started Checkout",
  "slots.requested": "Requested Slots",
  "booking.completed": "Booking Completed",
};

// booking.completed/booking.failed cover both payment methods (Pay After
// Service has no real "payment" step, so calling it payment.succeeded would
// be misleading); payment.failed here is narrower — only genuine online
// payment-gateway-attempt failures (order creation, gateway cancel/error).
const BREAKDOWN_EVENT_OPTIONS = [
  { value: "slots.fetch_failed", label: "Slot Fetch Failures" },
  { value: "slots.fetch_succeeded_empty", label: "Slot Fetch — No Slots Available" },
  { value: "booking.failed", label: "Booking Failures (both payment methods)" },
  { value: "payment.failed", label: "Online Payment Gateway Failures" },
];

const BREAKDOWN_PROPERTY_OPTIONS = [
  { value: "address", label: "Address" },
  { value: "error_code", label: "Error Code" },
];

const PLATFORM_LABELS = { ios: "iOS", android: "Android" };

const PlatformSummaryCards = ({ rows }) => (
  <div className="flex flex-col sm:flex-row gap-6">
    {rows.length === 0 ? (
      <div className="text-gray-500">No active users in this range yet.</div>
    ) : (
      rows.map((row) => (
        <div
          key={row.platform}
          className="bg-gray-800 rounded-xl px-6 py-4 flex-1"
        >
          <p className="text-gray-400 text-sm">
            {PLATFORM_LABELS[row.platform] || row.platform || "Unknown"} active users
          </p>
          <p className="text-white text-3xl font-bold mt-1">{row.uniqueUsers}</p>
        </div>
      ))
    )}
  </div>
);

const DAUChart = ({ rows }) => {
  const categories = rows.map((r) => r.date);
  const series = [{ name: "Unique Users", data: rows.map((r) => r.uniqueUsers) }];

  return (
    <Chart
      options={{
        chart: { id: "analytics-dau" },
        colors: ["#FBB034"],
        xaxis: {
          categories,
          labels: { style: { colors: "#FFFFFF" } },
        },
        yaxis: {
          labels: { style: { colors: "#FFFFFF" } },
        },
      }}
      series={series}
      type="line"
      width="100%"
      height={300}
    />
  );
};

const FunnelSection = ({ rows }) => {
  const base = rows[0]?.uniqueUsers || 1;

  return (
    <div className="flex flex-col space-y-3">
      {rows.map((step, index) => {
        const percentage = Math.round((step.uniqueUsers / base) * 100) || 0;
        const prevCount = index > 0 ? rows[index - 1].uniqueUsers : null;
        const dropCount = prevCount != null ? prevCount - step.uniqueUsers : null;

        return (
          <div key={step.eventName} className="flex flex-col">
            <div className="flex justify-between text-gray-300 text-sm mb-1 font-medium">
              <span>{FUNNEL_LABELS[step.eventName] || step.eventName}</span>
              <span>
                {step.uniqueUsers} ({percentage}%)
                {dropCount != null && dropCount > 0 && (
                  <span className="text-red-400 ml-2 text-xs">-{dropCount} dropped</span>
                )}
              </span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-4 relative overflow-hidden">
              <div
                className="bg-[#FBB033] h-full transition-all duration-500"
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// One row inside an expanded breakdown value — same shape/behavior as
// DropoffExplorerWidget's DropoffRow, just fed by getAnalyticsEventUsers
// instead of getAnalyticsDropoffUsers.
const EventUserRow = ({ row, fromEvent, toEvent, onSaved }) => {
  const [contacted, setContacted] = useState(row.contacted);
  const [notes, setNotes] = useState(row.notes || "");
  const [dirty, setDirty] = useState(false);

  const [saveContact, { loading: saving }] = useMutation(SET_ANALYTICS_DROPOFF_CONTACT);

  const handleSave = async () => {
    await saveContact({
      variables: { userId: row.userId, fromEvent, toEvent, contacted, notes },
    });
    setDirty(false);
    onSaved();
  };

  return (
    <tr className="border-b border-gray-800 align-top">
      <td className="py-1 pr-4 whitespace-nowrap">{row.contactNumber || "—"}</td>
      <td className="py-1 pr-4">
        {row.fullName || "—"}
        {row.existingBookingNote && (
          <div className="mt-1 inline-block bg-amber-900/40 text-amber-300 text-[10px] px-2 py-0.5 rounded-md">
            {row.existingBookingNote}
          </div>
        )}
      </td>
      <td className="py-1 pr-4 whitespace-nowrap font-mono text-[11px]">
        {row.lastEventAt ? new Date(row.lastEventAt).toLocaleString() : "—"}
      </td>
      <td className="py-1 pr-4 text-center">
        <input
          type="checkbox"
          checked={contacted}
          onChange={(e) => {
            setContacted(e.target.checked);
            setDirty(true);
          }}
        />
      </td>
      <td className="py-1 pr-4">
        <input
          type="text"
          className="bg-gray-800 text-gray-200 text-xs rounded-md px-2 py-1 border border-gray-700 w-full"
          placeholder="Notes (e.g. call outcome)"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setDirty(true);
          }}
        />
      </td>
      <td className="py-1">
        <button
          disabled={!dirty || saving}
          onClick={handleSave}
          className="bg-[#FBB033] text-black text-xs font-semibold px-3 py-1 rounded-md hover:opacity-90 disabled:opacity-40"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </td>
    </tr>
  );
};

// Users behind one breakdown row, e.g. everyone who hit
// "slots.fetch_succeeded_empty" at a given address — expanded inline when
// that row is clicked.
const EventUsersPanel = ({ eventName, groupByProperty, groupValue, filters }) => {
  const { data, loading, error, refetch } = useQuery(GET_ANALYTICS_EVENT_USERS, {
    variables: {
      eventName,
      groupByProperty,
      groupValue,
      startDate: filters.startDate,
      endDate: filters.endDate,
      limit: 100,
    },
    fetchPolicy: "network-only",
  });

  const rows = data?.getAnalyticsEventUsers?.data || [];
  const toEvent = `${groupByProperty}:${groupValue}`;

  return (
    <tr className="bg-gray-900/60">
      <td colSpan={2} className="p-4">
        {loading && <Loader />}
        {error && <div className="text-red-500 text-xs">Error loading users</div>}
        {!loading && rows.length === 0 && (
          <div className="text-gray-500 text-xs">No users found for this value.</div>
        )}
        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left text-gray-300">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="py-1 pr-4">Phone</th>
                  <th className="py-1 pr-4">Name</th>
                  <th className="py-1 pr-4">Last Seen</th>
                  <th className="py-1 pr-4">Contacted</th>
                  <th className="py-1 pr-4">Notes</th>
                  <th className="py-1"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <EventUserRow
                    key={row.userId}
                    row={row}
                    fromEvent={eventName}
                    toEvent={toEvent}
                    onSaved={refetch}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </td>
    </tr>
  );
};

const BreakdownTable = ({ rows, eventName, groupByProperty, filters, expandedValue, onToggleRow }) => (
  <div className="overflow-x-auto">
  <table className="w-full text-sm text-left text-gray-300">
    <thead>
      <tr className="border-b border-gray-700">
        <th className="py-2 pr-4">Value</th>
        <th className="py-2">Count</th>
      </tr>
    </thead>
    <tbody>
      {rows.length === 0 ? (
        <tr>
          <td colSpan={2} className="py-4 text-gray-500">
            No events in this range.
          </td>
        </tr>
      ) : (
        rows.map((row) => (
          <React.Fragment key={row.groupValue}>
            <tr
              className="border-b border-gray-800 cursor-pointer hover:bg-gray-800/60"
              onClick={() => onToggleRow(row.groupValue)}
            >
              <td className="py-2 pr-4 break-all">{row.groupValue}</td>
              <td className="py-2">{row.count}</td>
            </tr>
            {expandedValue === row.groupValue && (
              <EventUsersPanel
                eventName={eventName}
                groupByProperty={groupByProperty}
                groupValue={row.groupValue}
                filters={filters}
              />
            )}
          </React.Fragment>
        ))
      )}
    </tbody>
  </table>
  </div>
);

const ProductAnalyticsWidget = ({ filters }) => {
  const [breakdownEvent, setBreakdownEvent] = useState(BREAKDOWN_EVENT_OPTIONS[0].value);
  const [breakdownProperty, setBreakdownProperty] = useState(
    BREAKDOWN_PROPERTY_OPTIONS[0].value,
  );
  const [expandedValue, setExpandedValue] = useState(null);

  const dauQuery = useQuery(GET_ANALYTICS_DAU, {
    variables: { startDate: filters.startDate, endDate: filters.endDate },
    fetchPolicy: "network-only",
  });

  const platformQuery = useQuery(GET_ANALYTICS_PLATFORM_BREAKDOWN, {
    variables: { startDate: filters.startDate, endDate: filters.endDate },
    fetchPolicy: "network-only",
  });

  const funnelQuery = useQuery(GET_ANALYTICS_FUNNEL, {
    variables: {
      eventNames: FUNNEL_EVENT_NAMES,
      startDate: filters.startDate,
      endDate: filters.endDate,
    },
    fetchPolicy: "network-only",
  });

  const breakdownQuery = useQuery(GET_ANALYTICS_EVENT_BREAKDOWN, {
    variables: {
      eventName: breakdownEvent,
      groupByProperty: breakdownProperty,
      startDate: filters.startDate,
      endDate: filters.endDate,
      limit: 25,
    },
    fetchPolicy: "network-only",
  });

  const dauRows = dauQuery.data?.getAnalyticsDAU?.data || [];
  const platformRows = platformQuery.data?.getAnalyticsPlatformBreakdown?.data || [];
  const funnelRows = funnelQuery.data?.getAnalyticsFunnel?.data || [];
  const breakdownRows = breakdownQuery.data?.getAnalyticsEventBreakdown?.data || [];

  return (
    <div className="bg-[#1C1C1E] p-6 rounded-2xl shadow-lg border border-gray-800 flex flex-col space-y-8">
      <div>
        <h3 className="text-[#FBB033] font-bold text-xl mb-4">Active Users by Platform</h3>
        {platformQuery.loading ? (
          <Loader />
        ) : platformQuery.error ? (
          <div className="text-red-500">Error loading platform breakdown</div>
        ) : (
          <PlatformSummaryCards rows={platformRows} />
        )}
      </div>

      <div>
        <h3 className="text-[#FBB033] font-bold text-xl mb-4">Daily Active Users</h3>
        {dauQuery.loading ? (
          <Loader />
        ) : dauQuery.error ? (
          <div className="text-red-500">Error loading daily active users</div>
        ) : (
          <DAUChart rows={dauRows} />
        )}
      </div>

      <div>
        <h3 className="text-[#FBB033] font-bold text-xl mb-4">Booking Funnel</h3>
        {funnelQuery.loading ? (
          <Loader />
        ) : funnelQuery.error ? (
          <div className="text-red-500">Error loading funnel</div>
        ) : (
          <FunnelSection rows={funnelRows} />
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-[#FBB033] font-bold text-xl">Event Breakdown</h3>
          <div className="flex flex-wrap gap-3">
            <select
              className="bg-gray-800 text-gray-200 text-sm rounded-md px-2 py-1 border border-gray-700"
              value={breakdownEvent}
              onChange={(e) => {
                setBreakdownEvent(e.target.value);
                setExpandedValue(null);
              }}
            >
              {BREAKDOWN_EVENT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              className="bg-gray-800 text-gray-200 text-sm rounded-md px-2 py-1 border border-gray-700"
              value={breakdownProperty}
              onChange={(e) => {
                setBreakdownProperty(e.target.value);
                setExpandedValue(null);
              }}
            >
              {BREAKDOWN_PROPERTY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  Group by {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-gray-500 text-xs mb-2">Click a row to see who to call.</p>
        {breakdownQuery.loading ? (
          <Loader />
        ) : breakdownQuery.error ? (
          <div className="text-red-500">Error loading breakdown</div>
        ) : (
          <BreakdownTable
            rows={breakdownRows}
            eventName={breakdownEvent}
            groupByProperty={breakdownProperty}
            filters={filters}
            expandedValue={expandedValue}
            onToggleRow={(value) =>
              setExpandedValue((prev) => (prev === value ? null : value))
            }
          />
        )}
      </div>
    </div>
  );
};

export default ProductAnalyticsWidget;
